import { ReactNode, useMemo } from "react";
import cn from "classnames";
import { FCX } from "@datassential/platform-ui-lib";
import { getPercentInRange } from "@core/helpers";
import "./BarChart.scss";
import { isNumber } from "@core/formValidation";
import { NO_VALUE_PLACEHOLDER } from "@models";

export interface BarChartItem {
    id: number | string;
    name: string;
    value: number;
}

interface BarChartProps {
    data: BarChartItem[];
    formatValue?: (value: number) => string;
    renderBar?: (item: BarChartItem, index: number, barProps: BarRenderProps) => ReactNode;
    barsLegend?: ReactNode;
    activeBarId?: number | null;
    valueScaleExponent?: number;
    minBarHeightPercent?: number;
}

export interface BarRenderProps {
    className: string;
    style: { height: string };
    valueLabel: string;
}

const defaultFormatValue = (value: number) => isNumber(value) ? `${value}` : NO_VALUE_PLACEHOLDER;

const BarChart: FCX<BarChartProps> = ({
    data,
    formatValue = defaultFormatValue,
    renderBar,
    barsLegend,
    activeBarId,
    valueScaleExponent = 1,
    minBarHeightPercent = 3,
    className,
}) => {
    const { minValue, valueRange } = useMemo(() => {
        if (data.length === 0) {
            return { minValue: 0, valueRange: 0 };
        }

        const values = data.map(item => item.value || 0);
        const max = Math.max(...values);
        const min = Math.min(...values);

        return {
            minValue: min,
            valueRange: max - min,
        };
    }, [data]);

    return (
        <div className={cn("BarChart", className)}>
            <div className="BarChart__label"/>
            <div className="BarChart__bars">
                {barsLegend}
                {data.map((item, index) => {
                    const value = item.value || 0;
                    const heightPercent = valueRange > 0 ? getPercentInRange(value, minValue, minValue + valueRange) : 0;
                    const scaledPercent = valueScaleExponent === 1
                        ? heightPercent
                        : Math.pow(heightPercent / 100, valueScaleExponent) * 100;
                    const barHeight = Math.max(scaledPercent, minBarHeightPercent);
                    const isActive = activeBarId !== undefined && activeBarId !== null && item.id === activeBarId;
                    const barProps: BarRenderProps = {
                        className: cn('BarChart__bar', `appears-top del-${index + 1}`, isActive && 'is-active'),
                        style: { height: `${barHeight}%` },
                        valueLabel: formatValue(value),
                    };

                    if (renderBar) {
                        return renderBar(item, index, barProps);
                    }

                    return (
                        <div
                            key={`bar-${item.id || index}`}
                            className={barProps.className}
                            style={barProps.style}
                        >
                            <div className="BarChart__bar-value">
                                {barProps.valueLabel}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="BarChart__names-row">
                {data.map((item, index) => {
                    const isActive = activeBarId !== undefined && activeBarId !== null && item.id === activeBarId;
                    return (
                    <div
                        key={`name-${item.id || index}`}
                        className={cn('BarChart__bar-name-wrapper', `appears-top del-${index + 1}`, isActive && 'is-active')}
                    >
                        <span className="BarChart__bar-name">{item.name}</span>
                    </div>
                    );
                })}
            </div>
        </div>
    );
};

export default BarChart;
